"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAutoStatusManager } from '@/app/_module/hooks/useAutoStatusManager';

export interface ImportJob {
  id: string;
  fileName: string;
  totalLeads: number;
  processedLeads: number;
  status: 'processing' | 'completing' | 'assigning' | 'completed' | 'failed' | 'cooldown';
  message: string;
  stats?: {
    leadsCreated: number;
    /** Rows for people already in your leads (exact duplicates or merged into them). */
    alreadyInLeads?: number;
    /** Rows for someone this same file already created (an extra policy row). */
    repeatsInFile?: number;
    /** Every row in the file, whatever happened to it. */
    totalRows?: number;
    householdsCreated: number;
    householdMembersCreated: number;
    errors: number;
  };
  /** Failed rows, plus already-in-your-leads rows tagged kind: 'duplicate' (not errors). */
  errors?: Array<{ lead: any; error: string; kind?: 'duplicate' }>;
  startTime: Date;
  endTime?: Date;
  /**
   * The upload's "Assign to" pick: the agent's name, or null when nobody was picked (absent = an
   * agent column decides). The box shows it from the start, because a missing pick used to finish
   * as a plain "Complete" — Moe Ray's 2,847 out-of-state leads sat unassigned in MRFG, and to him
   * they had vanished (2026-10-13).
   */
  assignTo?: string | null;
  // Auto-assignment tracking
  assignmentStats?: {
    agentColumn: string | null;
    attempted: number;
    successful: number;
    failed: number;
    skipped: number;
    /** "Assign to" pick only: leads the agent already had. */
    alreadyTheirs?: number;
    /** "Assign to" pick only: leads another agent has, left with them. */
    heldByOthers?: number;
  };
}

interface ImportProgressContextType {
  activeImports: ImportJob[];
  startImport: (id: string, fileName: string, totalLeads: number, assignTo?: string | null) => void;
  updateProgress: (id: string, processedLeads: number, message: string) => void;
  startAssignment: (id: string, agentColumn: string) => void;
  completeAssignment: (id: string, assignmentStats: ImportJob['assignmentStats']) => void;
  completeImport: (id: string, stats: ImportJob['stats'], errors: ImportJob['errors']) => void;
  failImport: (id: string, error: string) => void;
  coolDownImport: (id: string, message: string) => void;
  dismissImport: (id: string) => void;
  clearAllImports: () => void;
}

const ImportProgressContext = createContext<ImportProgressContextType | undefined>(undefined);

export const ImportProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeImports, setActiveImports] = useState<ImportJob[]>([]);
  const queryClient = useQueryClient();
  const { pauseIdleTimer, resumeIdleTimer } = useAutoStatusManager();
  const isIdlePausedRef = useRef(false);

  // Pause idle timer when any import is processing, resume when all complete
  useEffect(() => {
    const hasActiveImport = activeImports.some(
      job => job.status === 'processing' || job.status === 'assigning' || job.status === 'completing'
    );

    if (hasActiveImport && !isIdlePausedRef.current) {
      // Pause idle timer during import
      pauseIdleTimer();
      isIdlePausedRef.current = true;
    } else if (!hasActiveImport && isIdlePausedRef.current) {
      // Resume idle timer when all imports complete
      resumeIdleTimer();
      isIdlePausedRef.current = false;
    }
  }, [activeImports, pauseIdleTimer, resumeIdleTimer]);

  const startImport = useCallback((id: string, fileName: string, totalLeads: number, assignTo?: string | null) => {
    const newImport: ImportJob = {
      id,
      fileName,
      totalLeads,
      processedLeads: 0,
      status: 'processing',
      message: 'Starting import...',
      startTime: new Date(),
      assignTo,
    };

    setActiveImports(prev => [...prev, newImport]);
  }, []);

  const updateProgress = useCallback((id: string, processedLeads: number, message: string) => {
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? { ...job, processedLeads, message }
          : job
      )
    );
  }, []);

  const startAssignment = useCallback((id: string, agentColumn: string) => {
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              status: 'assigning' as const,
              message: agentColumn === 'selected agent'
                ? 'Assigning to the agent you picked...'
                : `Assigning leads to agents (${agentColumn} column detected)...`,
            }
          : job
      )
    );
  }, []);

  const completeAssignment = useCallback((id: string, assignmentStats: ImportJob['assignmentStats']) => {
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              // The import's own "completed" waits for this (see completeImport).
              status: 'completed' as const,
              endTime: job.endTime ?? new Date(),
              assignmentStats,
              message: assignmentStats?.successful
                ? `Assigned ${assignmentStats.successful} leads to agents`
                : job.message,
            }
          : job
      )
    );
    setTimeout(() => {
      setActiveImports(prev => prev.filter(job => job.id !== id));
    }, 15000);
  }, []);

  const completeImport = useCallback((
    id: string,
    stats: ImportJob['stats'],
    errors: ImportJob['errors']
  ) => {
    // First, set to 'completing' status with processedLeads = totalLeads for 100% animation
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              status: 'completing' as const,
              processedLeads: job.totalLeads, // Set to 100% for smooth animation
              message: `Completing import...`,
              stats,
              errors,
            }
          : job
      )
    );

    // After animation completes (800ms), set to fully completed — unless the leads are still being
    // assigned. A 3,000-row assign outlasts this timer AND the 15 s auto-dismiss below, so the box went
    // green "Complete" and vanished before saying who got them; completeAssignment finishes it instead.
    setTimeout(() => {
      setActiveImports(prev =>
        prev.map(job =>
          job.id === id
            ? {
                ...job,
                status: job.status === 'assigning' ? job.status : 'completed' as const,
                message: job.status === 'assigning' ? job.message : `Successfully imported ${stats?.leadsCreated || 0} leads`,
                endTime: new Date(),
              }
            : job
        )
      );

      // Auto-dismiss after 15 seconds of completion (so widget shows for ~15 seconds total)
      setTimeout(() => {
        setActiveImports(prev => prev.filter(job => job.id !== id || job.status === 'assigning'));
      }, 15000);
    }, 800);

    // Invalidate queries to refresh leads list
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['households'] });
  }, [queryClient]);

  const failImport = useCallback((id: string, error: string) => {
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              status: 'failed' as const,
              message: error,
              endTime: new Date(),
            }
          : job
      )
    );
  }, []);

  // CSV imports are rate-limited (backend LeadImportThrottlerGuard → HTTP 429).
  // This is NOT a failure — the user just uploaded too fast. Show a distinct
  // amber "cooling down" state instead of a scary red "failed".
  const coolDownImport = useCallback((id: string, message: string) => {
    setActiveImports(prev =>
      prev.map(job =>
        job.id === id
          ? {
              ...job,
              status: 'cooldown' as const,
              message,
              endTime: new Date(),
            }
          : job
      )
    );
  }, []);

  const dismissImport = useCallback((id: string) => {
    setActiveImports(prev => prev.filter(job => job.id !== id));
  }, []);

  const clearAllImports = useCallback(() => {
    setActiveImports([]);
  }, []);

  return (
    <ImportProgressContext.Provider
      value={{
        activeImports,
        startImport,
        updateProgress,
        startAssignment,
        completeAssignment,
        completeImport,
        failImport,
        coolDownImport,
        dismissImport,
        clearAllImports,
      }}
    >
      {children}
    </ImportProgressContext.Provider>
  );
};

export const useImportProgress = () => {
  const context = useContext(ImportProgressContext);
  if (context === undefined) {
    throw new Error('useImportProgress must be used within an ImportProgressProvider');
  }
  return context;
};